/**
 * CrmClickableContent — wrapper that provides CRM slideout click
 * behavior to any object card content component.
 *
 * Wraps a single child element and injects an `onClick` prop that
 * opens the CRM slideout for the given entity. When the entity
 * doesn't exist in CRM (no entityId) or the slideout context isn't
 * available, the child renders unchanged.
 *
 * Usage:
 *   <CrmClickableContent entityId={id}>
 *     <ContactCardContent name="..." email="..." />
 *   </CrmClickableContent>
 */

import type { ReactElement } from 'react';
import { cloneElement, useCallback } from 'react';

import { useActivePreviewHandle } from '@/lib/crm/preview';

import { useCrmSlideout } from './useCrmSlideout';

interface CrmClickableContentProps {
  /** CRM entity ID — pass undefined when the entity doesn't exist in CRM */
  entityId: string | undefined;
  /** A single card content element that accepts an optional onClick prop */
  children: ReactElement<{ onClick?: () => void }>;
}

export function CrmClickableContent({
  entityId,
  children,
}: CrmClickableContentProps) {
  // Context inside a provider subtree; the ACTIVE registered provider
  // otherwise — so preview entities stay clickable when rendered inside the
  // out-of-subtree slideout (e.g. a projected record's association rows).
  const preview = useActivePreviewHandle();
  const handleClick = useCrmSlideout(entityId);
  const childOnClick = children.props.onClick;

  const composedClick = useCallback(() => {
    childOnClick?.();
    handleClick?.();
  }, [childOnClick, handleClick]);

  // A previewed id (a proposed create/edit not yet in the CRM) opens the
  // slideout via the preview projection, so treat it as clickable just like a
  // real entity (Decision 1 of the preview epic). `hasPreview` is false when no
  // preview provider is mounted (flag off), keeping real-entity behavior
  // unchanged. Card call-sites pass the minted id post-migration (sibling
  // issues); until then this stays inert for non-existing entities.
  const hasPreview = Boolean(entityId && preview?.hasPreview(entityId));

  if (!handleClick && !hasPreview) {
    return children;
  }
  return cloneElement(children, { onClick: composedClick });
}
